import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Accordion, Badge, Form, ListGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SampleLocationIndex } from '../../services/sample.service';
import {
  type CaseLocationCell,
  caseLocationCellKey,
  caseTreeCaseSearchHaystack,
  formatCaseTreeCaseLabel,
  formatCaseTreeLocationDisplay,
} from '../../utils/caseLocationUtils';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import type { LocationShareTarget } from '../../utils/locationShare';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../../utils/softBreakIdentifier';
import { formatUserName } from '../../utils/name.util';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';

type LocationRoom = SampleLocationIndex['storageRooms'][0];
type DrawerNode = LocationRoom['drawers'][0];

interface CaseLocationTreeProps {
  index: SampleLocationIndex;
  selected: CaseLocationCell | null;
  onSelect: (cell: CaseLocationCell) => void;
  currentUserId?: number | null;
  /** 保管庫所有者（または adminViewAll）のみ true */
  canShareRoom?: (storageRoomId: number) => boolean;
  onShareLocation?: (target: LocationShareTarget) => void;
}

function roomIdentityKey(room: LocationRoom): string {
  return `${room.name}\0${room.location || ''}`;
}

function roomNameKey(room: LocationRoom): string {
  return room.name.trim().toLowerCase();
}

function roomOwnerLabel(room: LocationRoom, language: string): string {
  if (!room.owner) return '';
  return (
    formatUserName(room.owner.firstName, room.owner.lastName, language) ||
    room.owner.username?.trim() ||
    ''
  );
}

/** 保管庫／引き出し／ケース行の件数・合計残（単位付き） */
export function formatCaseTreeCountLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  sampleCount: number,
  totalRemaining: number | null
): string {
  const countPart = t('sample.case_manager.sample_count', { count: sampleCount });
  if (totalRemaining === null) return countPart;
  return joinLabeledValue(
    countPart,
    joinLabeledValue(
      t('sample.remaining_quantity_short', { defaultValue: '残' }),
      formatSampleQuantityWithUnit(totalRemaining, null, t)
    )
  );
}

/** 保管庫アコーディオンの accessible name（表示短縮ロケーション・所有者を反映） */
export function formatCaseTreeRoomAriaLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  room: Pick<LocationRoom, 'name' | 'location' | 'sampleCount' | 'totalRemaining'>,
  options?: { ownerLabel?: string | null; includeOwner?: boolean }
): string {
  const shortLocation = formatCaseTreeLocationDisplay(room.location).display;
  const owner =
    options?.includeOwner && options.ownerLabel?.trim() ? options.ownerLabel.trim() : null;
  const meta = [
    shortLocation || null,
    owner,
    formatCaseTreeCountLabel(t, room.sampleCount, room.totalRemaining),
  ]
    .filter(Boolean)
    .join(' · ');
  const name = formatEntityDisplayName(room.name, t);
  return meta ? `${name} · ${meta}` : name;
}

function LevelIcon({ iconClassName }: { iconClassName: string }) {
  return (
    <span className="case-tree-level-icon" aria-hidden="true">
      <i className={`fas ${iconClassName}`} />
    </span>
  );
}


function CaseListItems({
  storageRoomId,
  drawerNumber,
  cases,
  selected,
  onSelect,
  searchTerm,
}: {
  storageRoomId: number | null;
  drawerNumber: number | null;
  cases: DrawerNode['cases'];
  selected: CaseLocationCell | null;
  onSelect: (cell: CaseLocationCell) => void;
  searchTerm: string;
}) {
  const { t } = useTranslation();
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedSearch) return cases;
    return cases.filter((c) =>
      caseTreeCaseSearchHaystack(c.caseName, drawerNumber, t)
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [cases, drawerNumber, normalizedSearch, t]);

  return (
    <ListGroup variant="flush">
      {filtered.map((caseItem) => {
        const cell: CaseLocationCell = {
          storageRoomId,
          drawerNumber,
          caseName: caseItem.caseName,
        };
        const isActive =
          selected != null && caseLocationCellKey(selected) === caseLocationCellKey(cell);
        const label = formatCaseTreeCaseLabel(caseItem.caseName, t);
        const counts = formatCaseTreeCountLabel(t, caseItem.sampleCount, caseItem.totalRemaining);
        const openTitle = t('sample.case_manager.open_case_title', {
          name: label,
          counts,
          defaultValue: `${t('sample.case_manager.open_in_sample_list')} · ${label} · ${counts}`,
        });
        const unassigned = caseItem.caseName == null;
        return (
          <ListGroup.Item
            key={`${drawerNumber ?? 'd'}-${caseItem.caseName ?? 'c'}`}
            action
            active={isActive}
            onClick={() => onSelect(cell)}
            className="case-location-case-item"
            aria-label={joinLabeledValue(label, counts)}
            title={openTitle}
          >
            <span
              className={`case-tree-case-label text-truncate${
                isActive ? ' text-white' : ''
              }${unassigned && !isActive ? ' case-tree-case-unassigned' : ''}`}
            >
              <LevelIcon iconClassName={unassigned ? 'fa-box-open' : 'fa-box'} />
              {softBreakIdentifier(label, t)}
              {' · '}
            </span>
            <span
              className={`case-tree-case-meta small text-nowrap${
                isActive ? ' text-white opacity-75' : ' text-muted'
              }`}
              aria-hidden="true"
            >
              {counts}
            </span>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
}

function DrawerBlocks({
  storageRoomId,
  drawers,
  selected,
  onSelect,
  searchTerm,
}: {
  storageRoomId: number | null;
  drawers: DrawerNode[];
  selected: CaseLocationCell | null;
  onSelect: (cell: CaseLocationCell) => void;
  searchTerm: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      {drawers.map((drawer) => {
        const drawerName =
          drawer.drawerNumber != null
            ? `#${drawer.drawerNumber}`
            : t('sample.case_manager.unassigned_drawer');
        const counts = formatCaseTreeCountLabel(t, drawer.sampleCount, drawer.totalRemaining);
        return (
          <div
            key={`${storageRoomId ?? 'none'}-${drawer.drawerNumber ?? 'none'}`}
            className="border-bottom case-tree-drawer"
          >
            <div
              className="case-tree-drawer-label px-3 py-2 bg-light small fw-semibold"
              aria-label={joinLabeledValue(drawerName, counts)}
            >
              <LevelIcon iconClassName="fa-inbox" />
              <span className="case-tree-drawer-name">
                {drawerName}
                {' · '}
              </span>
              <span className="case-tree-drawer-meta text-muted fw-normal" aria-hidden="true">
                {counts}
              </span>
            </div>
            <CaseListItems
              storageRoomId={storageRoomId}
              drawerNumber={drawer.drawerNumber}
              cases={drawer.cases}
              selected={selected}
              onSelect={onSelect}
              searchTerm={searchTerm}
            />
          </div>
        );
      })}
    </>
  );
}

function RoomHeaderBody({
  title,
  location,
  locationTitle,
  counts,
  titleBadges,
  iconClassName = 'fa-warehouse',
}: {
  title: ReactNode;
  /** 場所など（省略可）。件数はタイトル行右端で常時表示 */
  location?: string | null;
  /** title 属性用のフル場所（未指定時は location） */
  locationTitle?: string | null;
  counts: string;
  /** 共有・所有者など短いバッジ（タイトル行・名の直後。場所行では切れる） */
  titleBadges?: ReactNode;
  iconClassName?: string;
}) {
  const fullMeta = [locationTitle || location, counts].filter(Boolean).join(' · ');
  /* xs は件数を場所行へ移し、保管庫名（冷凍庫1/2）の識別幅を確保 */
  const showMeta = Boolean(location) || Boolean(counts);
  return (
    <div className="d-flex flex-column w-100 pe-2 min-w-0 case-tree-room-header-body">
      <span className="fw-semibold case-tree-room-title">
        <LevelIcon iconClassName={iconClassName} />
        <span className="case-tree-room-name sample-ellipsis-show-tail">
          <span className="sample-ellipsis-show-tail-inner">{title}</span>
        </span>
        {titleBadges}
        {/* 先頭 · は xs で --title ごと非表示（バッジ直後に · だけ残さない） */}
        <span className="case-tree-room-counts case-tree-room-counts--title small text-muted fw-normal">
          {' · '}
          {counts}
        </span>
      </span>
      {showMeta ? (
        <small className="text-muted case-tree-room-meta" title={fullMeta}>
          {location ? <span className="case-tree-room-location">{location}</span> : null}
          {/* 件数の先頭 · は sm+ で --meta ごと非表示になる（場所行に · だけ残さない） */}
          <span className="case-tree-room-counts case-tree-room-counts--meta">
            {location ? ' · ' : ''}
            {counts}
          </span>
        </small>
      ) : null}
    </div>
  );
}

/**
 * RB Accordion.Header は aria-* / title を h2 に載せ、フォーカス可能な button には渡さない。
 * 読み上げ名は visually-hidden、見た目は aria-hidden。button へは layout 後に同期する。
 */
function AccordionHeaderLabeled({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const btn = wrapRef.current?.querySelector<HTMLButtonElement>('button.accordion-button');
    if (!btn) return;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }, [label]);

  return (
    <div ref={wrapRef} className="case-tree-accordion-header-wrap">
      <Accordion.Header>
        <span className="visually-hidden">{label}</span>
        <div className="w-100" aria-hidden="true">
          {children}
        </div>
      </Accordion.Header>
    </div>
  );
}

export default function CaseLocationTree({
  index,
  selected,
  onSelect,
  currentUserId,
  canShareRoom,
  onShareLocation,
}: CaseLocationTreeProps) {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const ambiguousRoomKeys = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of index.storageRooms) {
      const key = roomIdentityKey(room);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([key]) => key));
  }, [index.storageRooms]);

  /** 同名保管庫は場所が違ってもタイトル行で所有者を見せる（xs で場所行が切れても判別できる） */
  const ambiguousRoomNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of index.storageRooms) {
      const key = roomNameKey(room);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([key]) => key));
  }, [index.storageRooms]);

  const defaultActiveKey = useMemo(() => {
    if (selected?.storageRoomId != null) {
      return `room-${selected.storageRoomId}`;
    }
    if (selected && selected.storageRoomId == null && index.withoutStorageRoom) {
      return 'room-none';
    }
    const firstWithSamples = index.storageRooms.find((r) => r.sampleCount > 0);
    if (firstWithSamples) return `room-${firstWithSamples.id}`;
    if (index.storageRooms[0]) return `room-${index.storageRooms[0].id}`;
    if (index.withoutStorageRoom && index.withoutStorageRoom.sampleCount > 0) {
      return 'room-none';
    }
    return undefined;
  }, [index, selected]);

  return (
    <div className="case-location-tree">
      <div className="input-group mb-2 case-tree-search">
        <span className="input-group-text bg-white text-muted" aria-hidden="true">
          <i className="fas fa-search" />
        </span>
        <Form.Control
          type="search"
          placeholder={t('sample.case_manager.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          aria-label={t('sample.case_manager.search_aria', {
            defaultValue: 'ケース検索',
          })}
          title={t('sample.case_manager.search_title', {
            defaultValue: 'ツリー内のケース名・保管庫名で絞り込み',
          })}
        />
      </div>

      <Accordion defaultActiveKey={defaultActiveKey} alwaysOpen flush>
        {index.storageRooms.map((room) => {
          const ownerLabel = roomOwnerLabel(room, i18n.language);
          const isOtherOwner =
            room.ownerUserId != null &&
            currentUserId != null &&
            room.ownerUserId !== currentUserId;
          const nameCollides = ambiguousRoomNames.has(roomNameKey(room));
          const identityCollides = ambiguousRoomKeys.has(roomIdentityKey(room));
          // 他ユーザー／同名衝突はタイトル行バッジ（場所行の ellipsis で消えない）
          const ownerBadgeLabel =
            isOtherOwner || nameCollides || identityCollides
              ? ownerLabel ||
                (isOtherOwner ? t('sample.case_manager.shared_by_other') : null)
              : null;
          const roomDisplayName = formatEntityDisplayName(room.name, t);
          // 同名保管庫は所有者を付けて共有ボタン同士を区別
          const shareName = ownerBadgeLabel
            ? `${roomDisplayName} · ${ownerBadgeLabel}`
            : roomDisplayName;
          const shareAria = t('sample.location_share_room_aria', {
            name: shareName,
            defaultValue: `${t('sample.external_share_links_short', { defaultValue: '共有' })} · ${shareName}`,
          });
          const shareTitle = t('sample.location_share_room_title', {
            name: shareName,
            defaultValue: `${t('sample.location_share_room_short', { defaultValue: '保管庫を共有' })} · ${shareName}`,
          });
          const canShare = Boolean(canShareRoom?.(room.id) && onShareLocation);
          const locFmt = formatCaseTreeLocationDisplay(room.location);
          const locationDisplay = locFmt.display || null;
          const locationTitle = locFmt.full || null;
          const roomAria = formatCaseTreeRoomAriaLabel(t, room, {
            ownerLabel,
            includeOwner: Boolean(ownerBadgeLabel),
          });
          return (
          <Accordion.Item eventKey={`room-${room.id}`} key={room.id}>
            {/* 共有は展開行ではなくヘッダ横アイコン（展開しなくても届く） */}
            <div className="case-tree-room-chrome">
              <div className="case-tree-room-chrome__header min-w-0">
                <AccordionHeaderLabeled label={roomAria}>
                  <RoomHeaderBody
                    title={softBreakIdentifier(room.name, t)}
                    location={locationDisplay}
                    locationTitle={locationTitle}
                    counts={formatCaseTreeCountLabel(t, room.sampleCount, room.totalRemaining)}
                    titleBadges={
                      <>
                        {room.isShared ? (
                          <Badge bg="info" text="dark" className="ms-1 align-middle flex-shrink-0">
                            {t('storage_room.is_shared_public')}
                          </Badge>
                        ) : null}
                        {ownerBadgeLabel ? (
                          <Badge
                            bg="light"
                            text="dark"
                            className="case-tree-owner-badge align-middle border ms-1 flex-shrink-0"
                            title={t('sample.case_manager.owned_by', {
                              name: ownerBadgeLabel,
                              defaultValue: `Owner · ${ownerBadgeLabel}`,
                            })}
                          >
                            {ownerBadgeLabel}
                          </Badge>
                        ) : null}
                      </>
                    }
                  />
                </AccordionHeaderLabeled>
              </div>
              {canShare ? (
                <button
                  type="button"
                  className="case-tree-share-aside"
                  title={shareTitle}
                  aria-label={shareAria}
                  onClick={() =>
                    onShareLocation?.({ scope: 'room', storageRoomId: room.id })
                  }
                >
                  <i className="fas fa-share-alt" aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <Accordion.Body className="p-0">
              <DrawerBlocks
                storageRoomId={room.id}
                drawers={room.drawers}
                selected={selected}
                onSelect={onSelect}
                searchTerm={searchTerm}
              />
            </Accordion.Body>
          </Accordion.Item>
          );
        })}

        {index.withoutStorageRoom && index.withoutStorageRoom.sampleCount > 0 ? (
          <Accordion.Item eventKey="room-none">
            <AccordionHeaderLabeled
              label={`${t('sample.case_manager.unassigned_room')} · ${formatCaseTreeCountLabel(
                t,
                index.withoutStorageRoom.sampleCount,
                index.withoutStorageRoom.totalRemaining
              )}`}
            >
              <RoomHeaderBody
                iconClassName="fa-box-open"
                title={t('sample.case_manager.unassigned_room')}
                counts={formatCaseTreeCountLabel(
                  t,
                  index.withoutStorageRoom.sampleCount,
                  index.withoutStorageRoom.totalRemaining
                )}
              />
            </AccordionHeaderLabeled>
            <Accordion.Body className="p-0">
              <DrawerBlocks
                storageRoomId={null}
                drawers={index.withoutStorageRoom.drawers}
                selected={selected}
                onSelect={onSelect}
                searchTerm={searchTerm}
              />
            </Accordion.Body>
          </Accordion.Item>
        ) : null}
      </Accordion>
    </div>
  );
}
